import * as Routes from '@/modules/routes';
import axios from 'axios';
import { useQueries, useQuery } from '@tanstack/react-query';
import useAssessments from '../useAssessments';
import useGradesTableStore from '../../../store/useGradesTableStore';
import { IAssessment, IQuestionAnswer } from '../../../types';
import { QueryKeys } from '../../../GradesTableQueryClientProvider';

// AIDEV-NOTE: shared by the prefetch fan-out and the per-assessment subscription below so the two
// cannot drift on key or queryFn — they must hit the same cache entry for react-query to dedupe.
const assessmentQuestionAnswersQuery = (assessmentId: number, classroomId: number) => ({
  queryKey: [QueryKeys.AssessmentQuestionAnswers, assessmentId, classroomId],
  queryFn: () => {
    const path = Routes.plato_api_assessment_progress_question_answers_path({
      assessment_id: assessmentId,
      classroom_id: classroomId,
      only: ['id', 'student_id'],
    });

    return axios
      .get<{ data: IQuestionAnswer[] }>(path)
      .then(res => res.data.data)
      .catch(err => console.error(err));
  },
});

export const useAllAssessmentQuestionAnswers = () => {
  const classroom = useGradesTableStore(state => state.classroom);
  const lesson = useGradesTableStore(state => state.lesson);

  const isEnabled = !!(classroom && lesson);

  const { data: assessments } = useAssessments(classroom.id, lesson?.id);

  return useQueries({
    queries: assessments.map(assessment => ({
      ...assessmentQuestionAnswersQuery(assessment.id, classroom.id),
      enabled: isEnabled,
    })),
  });
};

const useAssessmentQuestionAnswers = (assessment: IAssessment) => {
  const classroom = useGradesTableStore(state => state.classroom);

  const isEnabled = !!(assessment?.id && classroom?.id);

  const query = useQuery({
    ...assessmentQuestionAnswersQuery(assessment?.id, classroom?.id),
    enabled: isEnabled,
  });

  return { ...query, data: query.data || [], isEnabled };
};

export default useAssessmentQuestionAnswers;
